import { z } from 'zod';
import type { ContributorToken } from '@sleed/bizcommerce-api';

/**
 * Contributor (Συντελεστής) edit form. Mirrors the legacy admin popup editor
 * (Core/client/admin/src/pages/Contributors/Contributors/index.js): basic info (name/surname/
 * country/dob/sort/visible/frontExclusion/SEO urls/indexation flags/groups/ERP) + 5 image slots.
 * `fn`/`ln` are the default-language values saved top-level via the Accept-Language=el pin.
 * Server-owned fields not on the form survive via spread-on-update. Image bytes upload out-of-band.
 */
const imageFile = z.instanceof(File).optional().nullable();

export const contributorSchema = z.object({
	id: z.number().optional(),
	fn: z.string().trim().max(50, 'validation.tooLong').nullable(),
	ln: z.string().trim().max(50, 'validation.tooLong').nullable(),
	cid: z.number().nullable(),
	dob: z.string().nullable(),
	si: z.number().int().nullable(),
	iv: z.boolean(),
	frontExclusion: z.boolean(),
	eurl: z.string().trim().max(400, 'validation.tooLong').nullable(),
	landingUrl: z.string().trim().max(400, 'validation.tooLong').nullable(),
	erpcd: z.string().trim().max(50, 'validation.tooLong').nullable(),
	grouplst: z.array(z.number()),
	img1: z.string().nullable(),
	imgdel1: z.boolean().nullable(),
	img1File: imageFile,
	img2: z.string().nullable(),
	imgdel2: z.boolean().nullable(),
	img2File: imageFile,
	img3: z.string().nullable(),
	imgdel3: z.boolean().nullable(),
	img3File: imageFile,
	img4: z.string().nullable(),
	imgdel4: z.boolean().nullable(),
	img4File: imageFile,
	img5: z.string().nullable(),
	imgdel5: z.boolean().nullable(),
	img5File: imageFile
});

export type ContributorFormValues = z.infer<typeof contributorSchema>;

export const EMPTY_CONTRIBUTOR: ContributorFormValues = {
	fn: '',
	ln: '',
	cid: null,
	dob: null,
	si: 0,
	iv: true,
	frontExclusion: false,
	eurl: '',
	landingUrl: '',
	erpcd: '',
	grouplst: [],
	img1: null,
	imgdel1: null,
	img1File: null,
	img2: null,
	imgdel2: null,
	img2File: null,
	img3: null,
	imgdel3: null,
	img3File: null,
	img4: null,
	imgdel4: null,
	img4File: null,
	img5: null,
	imgdel5: null,
	img5File: null
};

export function tokenToForm(token: ContributorToken): ContributorFormValues {
	return {
		id: token.id,
		fn: token.fn ?? '',
		ln: token.ln ?? '',
		cid: token.cid ?? null,
		dob: (token.dob as string | null) ?? null,
		si: token.si ?? 0,
		iv: token.iv ?? false,
		frontExclusion: token.frontExclusion ?? false,
		eurl: token.eurl ?? '',
		landingUrl: token.landingUrl ?? '',
		erpcd: token.erpcd ?? '',
		grouplst: token.grouplst ?? [],
		img1: token.img1 ?? null,
		imgdel1: null,
		img1File: null,
		img2: token.img2 ?? null,
		imgdel2: null,
		img2File: null,
		img3: token.img3 ?? null,
		imgdel3: null,
		img3File: null,
		img4: token.img4 ?? null,
		imgdel4: null,
		img4File: null,
		img5: token.img5 ?? null,
		imgdel5: null,
		img5File: null
	};
}

export function formToToken(form: ContributorFormValues, original?: ContributorToken | null): ContributorToken {
	return {
		...(original ?? {}),
		id: form.id,
		fn: form.fn?.trim() || '',
		ln: form.ln?.trim() || '',
		cid: form.cid,
		dob: form.dob,
		si: form.si ?? 0,
		iv: form.iv,
		frontExclusion: form.frontExclusion,
		eurl: form.eurl?.trim() || null,
		landingUrl: form.landingUrl?.trim() || null,
		erpcd: form.erpcd?.trim() || null,
		grouplst: form.grouplst,
		// Image URLs are owned by the upload pipeline; pass the delete flags through.
		imgdel1: form.imgdel1,
		imgdel2: form.imgdel2,
		imgdel3: form.imgdel3,
		imgdel4: form.imgdel4,
		imgdel5: form.imgdel5
	};
}

/** The image slots, used by both the editor (upload) and the save loop. */
export const CONTRIBUTOR_IMAGE_SLOTS = [
	{ key: '1', urlField: 'img1', fileField: 'img1File', delField: 'imgdel1' },
	{ key: '2', urlField: 'img2', fileField: 'img2File', delField: 'imgdel2' },
	{ key: '3', urlField: 'img3', fileField: 'img3File', delField: 'imgdel3' },
	{ key: '4', urlField: 'img4', fileField: 'img4File', delField: 'imgdel4' },
	{ key: '5', urlField: 'img5', fileField: 'img5File', delField: 'imgdel5' }
] as const;
